import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  WifiOff,
  QrCode,
  RefreshCw,
  Power,
  PowerOff,
  AlertTriangle,
  CheckCircle,
  MessageSquare
} from 'lucide-react';
import apiService from '../services/api';
import { generateWebhookUrl } from '../utils/webhookUtils';
import { useAuth } from '../contexts/AuthContext';
import { ModernHeader, ModernCard } from '../components/Modern';
import PageTransition from '../components/PageTransition';
import { useTheme } from '../contexts/ThemeContext';

const CrmConectar = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [instanceStatus, setInstanceStatus] = useState(null);
  const [instanceExists, setInstanceExists] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'info' });
  
  const instanceName = user?.target_db_name || 'principal';
  const WEBHOOK_URL = generateWebhookUrl(user?.target_db_name || 'default');

  const showNotification = useCallback((message, type = 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'info' });
    }, 5000);
  }, []);

  const translateStatus = (status) => {
    const statusMap = {
      'open': 'Conectado',
      'close': 'Desconectado',
      'connecting': 'Conectando...',
      'unknown': 'Desconhecido'
    };
    return statusMap[status] || status;
  };

  const getQRCode = useCallback(async (showLoadingIndicator = true) => {
    if (showLoadingIndicator) {
      setLoading(true);
    }
    
    try {
      const response = await apiService.getEvolutionQRCode(instanceName);
      
      if (response.success && response.data && response.data.base64) {
        setQrCode(response.data);
        showNotification('QR Code obtido com sucesso! Escaneie com seu WhatsApp.', 'success');
      } else {
        setQrCode(null);
        showNotification('Não foi possível obter o QR Code. Verifique se a instância está configurada.', 'warning');
      }
    } catch (error) {
      setQrCode(null);
      showNotification('Erro ao obter QR Code: ' + (error.message || 'Erro desconhecido'), 'error');
    }
    
    if (showLoadingIndicator) {
      setLoading(false);
    }
  }, [showNotification, instanceName]);

  const checkInstanceStatus = useCallback(async (skipQRCode = false, showLoadingIndicator = true) => {
    const startTime = Date.now();
    const minLoadingTime = 1500;
    
    try {
      const response = await apiService.getEvolutionInstanceStatus(instanceName);
      
      if (response.success && response.data) {
        const instanceData = response.data;
        const currentStatus = instanceData.connectionStatus || 'unknown';
        
        setInstanceExists(true);
        setInstanceStatus({
      instanceName: instanceData.name || instanceName,
          state: currentStatus,
          status: currentStatus
        });
        
        if (currentStatus === 'open') {
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
          
          setTimeout(() => {
            showNotification('WhatsApp conectado! Redirecionando para o CRM...', 'success');
            setQrCode(null);
            navigate('/crm');
          }, remainingTime);
          return;
        } else if (!skipQRCode && (currentStatus === 'close' || currentStatus === 'connecting')) {
          await getQRCode(true);
        }
      } else {
        setInstanceExists(false);
        setInstanceStatus(null);
        setQrCode(null);
      }
    } catch (error) {
      setInstanceExists(false);
      setInstanceStatus(null);
      setQrCode(null);
    }
    
    const elapsedTime = Date.now() - startTime;
    const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
    
    setTimeout(() => {
      if (showLoadingIndicator) {
        setLoading(false);
      }
      if (initialLoading) {
        setInitialLoading(false);
      }
    }, remainingTime);
  }, [getQRCode, initialLoading, showNotification, navigate, instanceName]);

  useEffect(() => {
    checkInstanceStatus();
  }, [checkInstanceStatus]);

  useEffect(() => {
    let intervalId = null;
    
    const shouldPoll = !initialLoading && instanceExists && (
      qrCode || 
      (instanceStatus && (instanceStatus.state === 'connecting' || instanceStatus.state === 'close'))
    );
    
    if (shouldPoll) {
      intervalId = setInterval(() => {
        checkInstanceStatus(false, false);
      }, 5000);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [qrCode, instanceStatus, instanceExists, initialLoading, checkInstanceStatus]);

  const createInstance = async () => {
    setCreating(true);
    try {
      const instanceData = {
      instanceName: instanceName,
        qrcode: true,
        integration: 'WHATSAPP-BAILEYS',
        webhook: {
          url: WEBHOOK_URL,
          events: [
            'APPLICATION_STARTUP',
            'QRCODE_UPDATED',
            'MESSAGES_UPSERT',
            'MESSAGES_UPDATE',
            'MESSAGES_DELETE',
            'SEND_MESSAGE',
            'CONTACTS_SET',
            'CONTACTS_UPSERT',
            'CONTACTS_UPDATE',
            'PRESENCE_UPDATE',
            'CHATS_SET',
            'CHATS_UPSERT',
            'CHATS_UPDATE',
            'CHATS_DELETE',
            'GROUPS_UPSERT',
            'GROUP_UPDATE',
            'GROUP_PARTICIPANTS_UPDATE',
            'CONNECTION_UPDATE',
            'CALL',
            'NEW_JWT_TOKEN'
          ]
        },
        settings: {
          reject_call: false,
          msg_call: '',
          groups_ignore: true,
          always_online: false,
          read_messages: false,
          read_status: false,
          sync_full_history: true
        }
      };

      const response = await apiService.createEvolutionInstance(instanceData);
      if (response.success) {
        showNotification('Instância criada com sucesso! Verificando status...', 'success');
        setTimeout(() => {
          checkInstanceStatus();
        }, 2000);
      } else {
        showNotification('Erro ao criar instância: ' + (response.message || 'Erro desconhecido'), 'error');
      }
    } catch (error) {
      showNotification('Erro ao criar instância: ' + (error.message || 'Erro desconhecido'), 'error');
    }
    setCreating(false);
  };

  const deleteInstance = async () => {
    setDeleting(true);
    try {
      const response = await apiService.deleteEvolutionInstance(instanceName);
      if (response.success) {
        showNotification('Instância excluída com sucesso! Recarregando página...', 'success');
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        showNotification('Erro ao excluir instância: ' + (response.message || 'Erro desconhecido'), 'error');
        setDeleting(false);
      }
    } catch (error) {
      showNotification('Erro ao excluir instância: ' + (error.message || 'Erro desconhecido'), 'error');
      setDeleting(false);
    }
  };

  if (initialLoading) {
    return (
      <PageTransition>
        <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} p-6`}>
          <div className="max-w-7xl mx-auto space-y-8">
            <ModernHeader 
              title="CRM - Configuração WhatsApp" 
              subtitle="Verificando status da conexão..." 
              icon={MessageSquare}
              themeColor="purple"
            />
            
            <ModernCard className="p-8" themeColor="purple" gradient glow>
              <div className="text-center">
                <RefreshCw className="w-12 h-12 text-purple-600 mx-auto mb-4 animate-spin" />
                <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Verificando status da conexão...
                </h3>
                <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                  Aguarde enquanto verificamos sua instância do WhatsApp
                </p>
              </div>
            </ModernCard>
          </div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} p-6`}>
        <div className="max-w-7xl mx-auto space-y-8">
          <ModernHeader 
            title="CRM - Configuração WhatsApp" 
            subtitle="Configure e gerencie sua conexão com o WhatsApp Business" 
            icon={MessageSquare}
            themeColor="purple"
          />
          
          <div className="mb-6">
            <ModernCard themeColor="purple">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-purple-600 mr-3" />
                <p className={`text-sm ${isDarkMode ? 'text-purple-200' : 'text-purple-800'}`}>
                  <strong>Importante:</strong> Para usar o CRM, você precisa ter uma instância do WhatsApp conectada.
                </p>
              </div>
            </ModernCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <ModernCard themeColor="purple" gradient glow>
              <div className="flex items-center mb-4">
                <WifiOff className="w-6 h-6 text-purple-600 mr-3" />
                <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Status da Instância</h2>
              </div>
              
              <div className="space-y-4">
                {instanceStatus ? (
                  <div className={`p-4 rounded-lg border ${
                    instanceStatus.state === 'open' 
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                      : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                  }`}>
                    <div className="flex items-center">
                      {instanceStatus.state === 'open' ? (
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
                      )}
                      <span className={`font-medium ${
                        instanceStatus.state === 'open' 
                          ? 'text-green-800 dark:text-green-200'
                          : 'text-yellow-800 dark:text-yellow-200'
                      }`}>
                        {translateStatus(instanceStatus.state)}
                      </span>
                    </div>
                    <p className={`text-sm mt-1 ${
                      instanceStatus.state === 'open' 
                        ? 'text-green-700 dark:text-green-300'
                        : 'text-yellow-700 dark:text-yellow-300'
                    }`}>
                      Instância: {instanceStatus.instanceName || 'principal'}
                    </p>
                  </div>
                ) : (
                  <div className="p-4 rounded-lg border bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
                    <div className="flex items-center">
                      <WifiOff className="w-5 h-5 text-gray-500 mr-2" />
                      <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Nenhuma instância encontrada
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </ModernCard>

            <ModernCard themeColor="purple" gradient glow>
              <div className="flex items-center mb-4">
                <Power className="w-6 h-6 text-purple-600 mr-3" />
                <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Gerenciar Instância</h2>
              </div>
              
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {instanceExists 
                  ? 'Gerencie sua instância atual do WhatsApp.' 
                  : 'Crie uma nova instância do WhatsApp para começar.'}
              </p>
              
              {!instanceExists ? (
                <button
                  onClick={createInstance}
                  disabled={creating || loading}
                  className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-purple-500 to-violet-600 text-white rounded-lg hover:from-purple-600 hover:to-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
                >
                  <Power className="w-5 h-5 mr-2" />
                  {creating ? 'Criando Instância...' : 'Criar Instância WhatsApp'}
                </button>
              ) : (
                <button
                  onClick={deleteInstance}
                  disabled={deleting || loading}
                  className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
                >
                  <PowerOff className="w-5 h-5 mr-2" />
                  {deleting ? 'Excluindo Instância...' : 'Excluir Instância WhatsApp'}
                </button>
              )}
            </ModernCard>
          </div>

          <ModernCard className="max-w-2xl mx-auto" themeColor="purple" gradient glow>
            <div className="mb-6">
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Conectar WhatsApp ao CRM
              </h2>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Configure sua instância do WhatsApp para começar a usar o CRM
              </p>
            </div>
        
            <div className="p-6">
              <div className="space-y-6">
                <div className="text-center">
                  <div className="flex items-center justify-center space-x-2 mb-4">
                    {instanceExists ? (
                      <CheckCircle className="w-8 h-8 text-green-500" />
                    ) : (
                      <WifiOff className="w-8 h-8 text-red-500" />
                    )}
                    <span className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {instanceExists ? 'Instância Criada' : 'Instância Não Encontrada'}
                    </span>
                  </div>
                  
                  <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
      Instância: <code className={`px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>{instanceName}</code>
                  </p>
                  
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Status: <span className={`font-medium ${instanceStatus?.state === 'open' ? 'text-green-600' : 'text-red-600'}`}>
                      {translateStatus(instanceStatus?.state) || 'Desconhecido'}
                    </span>
                  </p>
                </div>

                {qrCode && (
                  <div className="text-center">
                    <h3 className={`text-lg font-medium mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>QR Code para Conexão</h3>
                    <div className="flex justify-center mb-4">
                      <div className="bg-white p-4 rounded-xl shadow-lg">
                        <img 
                          src={qrCode.base64} 
                          alt="QR Code WhatsApp" 
                          className="w-64 h-64 object-contain"
                        />
                      </div>
                    </div>
                    <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Escaneie este QR Code com seu WhatsApp para conectar
                    </p>
                    <button
                      onClick={() => getQRCode(true)}
                      className="text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 text-sm font-medium"
                    >
                      Atualizar QR Code
                    </button>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {!instanceExists && (
                    <button
                      onClick={createInstance}
                      disabled={creating || loading}
                      className="md:col-span-2 flex items-center justify-center px-6 py-3 bg-gradient-to-r from-purple-500 to-violet-600 text-white rounded-lg hover:from-purple-600 hover:to-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
                    >
                      <Power className="w-5 h-5 mr-2" />
                      {creating ? 'Criando Instância...' : 'Criar Instância WhatsApp'}
                    </button>
                  )}

                  {instanceExists && !qrCode && instanceStatus?.state !== 'open' && (
                    <button
                      onClick={() => getQRCode(true)}
                      disabled={loading}
                      className="flex items-center justify-center px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
                    >
                      <QrCode className="w-5 h-5 mr-2" />
                      {loading ? 'Obtendo QR Code...' : 'Obter QR Code'}
                    </button>
                  )}

                  <button
                    onClick={() => checkInstanceStatus(false, true)}
                    disabled={loading}
                    className="flex items-center justify-center px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-lg hover:from-gray-600 hover:to-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
                  >
                    <RefreshCw className={`w-5 h-5 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    {loading ? 'Verificando Status...' : 'Verificar Status'}
                  </button>

                  {instanceStatus?.state === 'open' && (
                    <button
                      onClick={() => navigate('/crm')}
                      className="md:col-span-2 flex items-center justify-center px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-lg"
                    >
                      <MessageSquare className="w-5 h-5 mr-2" />
                      Acessar CRM Completo
                    </button>
                  )}
                </div>

                {notification && notification.show && (
                  <div className={`p-4 rounded-lg border ${
                    notification.type === 'error' 
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' 
                      : notification.type === 'warning'
                        ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                        : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                  }`}>
                    <p className={
                      notification.type === 'error' 
                        ? 'text-red-700 dark:text-red-300' 
                        : notification.type === 'warning'
                          ? 'text-yellow-700 dark:text-yellow-300'
                          : 'text-green-700 dark:text-green-300'
                    }>
                      {notification.message}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </ModernCard>
        </div>
      </div>
    </PageTransition>
  );
};

export default CrmConectar;